import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Menu,
  Plus,
  Loader2,
  Edit,
  Trash2,
  GripVertical,
  ChevronRight,
  ChevronDown,
  RotateCcw,
  FolderPlus,
  FilePlus,
  AlertTriangle,
  ShieldAlert,
  LayoutDashboard,
  GitCompare,
  ClipboardList,
  FileStack,
  Users,
  Settings,
  Scale,
  Mail,
  HelpCircle,
  Building2,
  BarChart3,
  FileSpreadsheet,
  Package,
  Send,
  FileText,
  Clock,
  Ticket,
  MessageSquare,
  AlertCircle,
  UserCog,
  FormInput,
  CalendarDays,
  UserPlus,
  Shield,
  PieChart,
  Home,
  Folder,
  File,
  Database,
  Globe,
  Lock,
  Key,
  Bell,
  Search,
  Filter,
  Download,
  Upload,
  Check,
  X,
  Star,
  Heart,
  Bookmark,
  Tag,
  Flag,
  Award,
  Target,
  Circle,
  ArrowUpDown,
  Save
} from "lucide-react";

// Map icon names to components
const ICON_MAP = {
  ShieldAlert, LayoutDashboard, GitCompare, ClipboardList, FileStack,
  Users, CalendarDays, Clock, UserPlus, Scale, Building2, Shield,
  BarChart3, FileSpreadsheet, Mail, Package, Send, FileText,
  HelpCircle, Ticket, MessageSquare, AlertCircle, Settings, UserCog,
  FormInput, PieChart, Home, Folder, File, Database, Globe,
  Lock, Key, Bell, Search, Filter, Download, Upload, Menu,
  Check, X, ChevronRight, ChevronDown, Circle, Star, Heart, 
  Bookmark, Tag, Flag, Award, Target, Plus, Edit, Trash2,
  GripVertical, RotateCcw, FolderPlus, FilePlus, AlertTriangle,
  ArrowUpDown, Save
};

const getIconComponent = (iconName) => ICON_MAP[iconName] || Circle;

// Sortable Menu Item Component
function SortableMenuItem({ menu, children, isExpanded, onToggle, onEdit, onDelete, onAddSubmenu }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: menu.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 'auto'
  };
  
  const MenuIcon = getIconComponent(menu.icon);
  
  return (
    <Card 
      ref={setNodeRef} 
      style={style}
      className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 ${isDragging ? 'shadow-lg ring-2 ring-indigo-500' : ''}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 touch-none"
              data-testid={`drag-handle-${menu.id}`}
            >
              <GripVertical className="w-5 h-5 text-slate-400" />
            </button>
            <div 
              className="flex items-center gap-3 cursor-pointer flex-1"
              onClick={onToggle}
            >
              <div className="w-10 h-10 rounded-lg bg-[#0C314C] flex items-center justify-center">
                <MenuIcon className="w-5 h-5 text-[#45E69A]" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  {menu.label}
                  <span className="text-xs font-normal text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                    {menu.id}
                  </span>
                </CardTitle>
                <p className="text-xs text-slate-500">
                  {menu.submenus?.length || 0} submenús • Orden: {menu.order}
                </p>
              </div>
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-slate-400 ml-2" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400 ml-2" />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onAddSubmenu}
              className="text-[#0C314C] hover:bg-[#0C314C]/10"
              data-testid={`add-submenu-${menu.id}`}
            >
              <FilePlus className="w-4 h-4 mr-1" />
              Submenú
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              className="text-slate-500 hover:text-slate-700"
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-slate-400 hover:text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      {children}
    </Card>
  );
}

// Sortable Submenu Item Component
function SortableSubmenuItem({ submenu, menuId, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: `${menuId}-${submenu.id}` });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  
  const SubIcon = getIconComponent(submenu.icon);
  
  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 ${isDragging ? 'bg-indigo-50 ring-2 ring-indigo-300' : ''}`}
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 touch-none"
          data-testid={`drag-submenu-${submenu.id}`}
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </button>
        <SubIcon className="w-4 h-4 text-slate-400" />
        <div>
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {submenu.label}
          </span>
          <span className="text-xs text-slate-400 ml-2">
            {submenu.id}
          </span>
          <p className="text-xs text-slate-400">
            Ruta: {submenu.path} • Orden: {submenu.order}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="text-slate-400 hover:text-slate-600 h-8 w-8 p-0"
        >
          <Edit className="w-3 h-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-slate-400 hover:text-red-600 h-8 w-8 p-0"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminMenusPage() {
  const [menus, setMenus] = useState([]);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  
  // Dialog states
  const [showMenuDialog, setShowMenuDialog] = useState(false);
  const [showSubmenuDialog, setShowSubmenuDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  
  const [editingMenu, setEditingMenu] = useState(null);
  const [editingSubmenu, setEditingSubmenu] = useState(null);
  const [parentMenuId, setParentMenuId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [menuForm, setMenuForm] = useState({ id: "", label: "", icon: "Folder", order: 0 });
  const [submenuForm, setSubmenuForm] = useState({ id: "", label: "", icon: "Circle", path: "", order: 0 });
  
  const { getAuthHeaders } = useAuth();

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchMenus = useCallback(async () => {
    try {
      setLoading(true);
      const [menusRes, iconsRes] = await Promise.all([
        axios.get(`${API}/system/menus`, getAuthHeaders()),
        axios.get(`${API}/system/menus/icons`, getAuthHeaders())
      ]);
      setMenus(menusRes.data || []);
      setAvailableIcons(iconsRes.data || []);
    } catch (err) {
      console.error("Error fetching menus:", err);
      toast.error("Error al cargar los menús");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  const toggleExpand = (menuId) => {
    setExpandedMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  // Drag and Drop handlers
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    // Check if we're dragging menus
    const activeIsMenu = menus.some(m => m.id === active.id);
    const overIsMenu = menus.some(m => m.id === over.id);
    
    if (activeIsMenu && overIsMenu) {
      // Reorder menus
      const oldIndex = menus.findIndex(m => m.id === active.id);
      const newIndex = menus.findIndex(m => m.id === over.id);
      
      const newMenus = arrayMove(menus, oldIndex, newIndex).map((menu, idx) => ({
        ...menu,
        order: idx + 1
      }));
      
      setMenus(newMenus);
      setHasOrderChanges(true);
    } else {
      // Check if we're dragging submenus within the same menu
      const activeMenuId = active.id.toString().split('-')[0];
      const overMenuId = over.id.toString().split('-')[0];
      
      if (activeMenuId === overMenuId) {
        const menuIdx = menus.findIndex(m => m.id === activeMenuId);
        if (menuIdx === -1) return;
        
        const menu = menus[menuIdx];
        const activeSubmenuId = active.id.toString().replace(`${activeMenuId}-`, '');
        const overSubmenuId = over.id.toString().replace(`${overMenuId}-`, '');
        
        const oldIndex = menu.submenus.findIndex(s => s.id === activeSubmenuId);
        const newIndex = menu.submenus.findIndex(s => s.id === overSubmenuId);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          const newSubmenus = arrayMove(menu.submenus, oldIndex, newIndex).map((sub, idx) => ({
            ...sub,
            order: idx + 1
          }));
          
          const newMenus = [...menus];
          newMenus[menuIdx] = { ...menu, submenus: newSubmenus };
          
          setMenus(newMenus);
          setHasOrderChanges(true);
        }
      }
    }
  };

  const saveOrderChanges = async () => {
    setSavingOrder(true);
    try {
      // Update all menus with new order
      await Promise.all(menus.map(async (menu) => {
        await axios.put(`${API}/system/menus/${menu.id}`, {
          label: menu.label,
          icon: menu.icon,
          order: menu.order,
          submenus: menu.submenus
        }, getAuthHeaders());
      }));
      
      toast.success("Orden de menús guardado correctamente");
      setHasOrderChanges(false);
    } catch (err) {
      console.error("Error saving order:", err);
      toast.error("Error al guardar el orden de los menús");
    } finally {
      setSavingOrder(false);
    }
  };

  // Menu CRUD
  const openNewMenuDialog = () => {
    setEditingMenu(null);
    setMenuForm({ id: "", label: "", icon: "Folder", order: menus.length + 1 });
    setShowMenuDialog(true);
  };

  const openEditMenuDialog = (menu) => {
    setEditingMenu(menu);
    setMenuForm({ id: menu.id, label: menu.label, icon: menu.icon, order: menu.order || 0 });
    setShowMenuDialog(true);
  };

  const handleSaveMenu = async () => {
    if (!menuForm.id.trim() || !menuForm.label.trim()) {
      toast.error("ID y nombre del menú son requeridos");
      return;
    }

    // Validate ID format (lowercase, no spaces)
    const idRegex = /^[a-z_]+$/;
    if (!editingMenu && !idRegex.test(menuForm.id)) {
      toast.error("El ID debe ser en minúsculas y sin espacios (use guiones bajos)");
      return;
    }

    setSaving(true);
    try {
      if (editingMenu) {
        await axios.put(`${API}/system/menus/${editingMenu.id}`, {
          label: menuForm.label,
          icon: menuForm.icon,
          order: menuForm.order
        }, getAuthHeaders());
        toast.success("Menú actualizado");
      } else {
        await axios.post(`${API}/system/menus`, menuForm, getAuthHeaders());
        toast.success("Menú creado");
      }
      setShowMenuDialog(false);
      fetchMenus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el menú");
    } finally {
      setSaving(false);
    }
  };

  // Submenu CRUD
  const openNewSubmenuDialog = (menuId) => {
    setParentMenuId(menuId);
    setEditingSubmenu(null);
    const parentMenu = menus.find(m => m.id === menuId);
    const order = (parentMenu?.submenus?.length || 0) + 1;
    setSubmenuForm({ id: "", label: "", icon: "Circle", path: "", order });
    setShowSubmenuDialog(true);
  };

  const openEditSubmenuDialog = (menuId, submenu) => {
    setParentMenuId(menuId);
    setEditingSubmenu(submenu);
    setSubmenuForm({ 
      id: submenu.id, 
      label: submenu.label, 
      icon: submenu.icon, 
      path: submenu.path,
      order: submenu.order || 0 
    });
    setShowSubmenuDialog(true);
  };

  const handleSaveSubmenu = async () => {
    if (!submenuForm.id.trim() || !submenuForm.label.trim() || !submenuForm.path.trim()) {
      toast.error("ID, nombre y ruta del submenú son requeridos");
      return;
    }

    const idRegex = /^[a-z_]+$/;
    if (!editingSubmenu && !idRegex.test(submenuForm.id)) {
      toast.error("El ID debe ser en minúsculas y sin espacios (use guiones bajos)");
      return;
    }

    setSaving(true);
    try {
      if (editingSubmenu) {
        await axios.put(
          `${API}/system/menus/${parentMenuId}/submenus/${editingSubmenu.id}`,
          { label: submenuForm.label, icon: submenuForm.icon, path: submenuForm.path, order: submenuForm.order },
          getAuthHeaders()
        );
        toast.success("Submenú actualizado");
      } else {
        await axios.post(
          `${API}/system/menus/${parentMenuId}/submenus`,
          submenuForm,
          getAuthHeaders()
        );
        toast.success("Submenú creado");
      }
      setShowSubmenuDialog(false);
      fetchMenus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el submenú");
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const openDeleteDialog = (type, menuId, submenuId = null) => {
    setDeleteTarget({ type, menuId, submenuId });
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setSaving(true);
    try {
      if (deleteTarget.type === "menu") {
        await axios.delete(`${API}/system/menus/${deleteTarget.menuId}`, getAuthHeaders());
        toast.success("Menú eliminado");
      } else {
        await axios.delete(
          `${API}/system/menus/${deleteTarget.menuId}/submenus/${deleteTarget.submenuId}`,
          getAuthHeaders()
        );
        toast.success("Submenú eliminado");
      }
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      fetchMenus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/system/menus/reset`, {}, getAuthHeaders());
      toast.success("Menús restaurados a configuración por defecto");
      setShowResetDialog(false);
      fetchMenus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al restaurar menús");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="admin-menus-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Administrar Menús
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Configura la estructura de navegación del sistema
            </p>
          </div>
          <div className="flex gap-2">
            {hasOrderChanges && (
              <Button
                onClick={saveOrderChanges}
                disabled={savingOrder}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="save-order-button"
              >
                {savingOrder ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Orden
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowResetDialog(true)}
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
              data-testid="reset-menus-button"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restaurar Defecto
            </Button>
            <Button 
              onClick={openNewMenuDialog}
              className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              data-testid="new-menu-button"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Nuevo Menú
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="mb-6 border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <ArrowUpDown className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">Arrastra y suelta para reordenar:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-600 dark:text-blue-400">
                  <li>Usa el icono <GripVertical className="w-4 h-4 inline" /> para arrastrar menús y submenús</li>
                  <li>Los cambios de orden se guardan al hacer clic en "Guardar Orden"</li>
                  <li>Los nuevos menús/submenús aparecerán sin acceso por defecto en los roles</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Menus List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={menus.map(m => m.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {menus.map((menu) => {
                  const isExpanded = expandedMenus[menu.id];
                  
                  return (
                    <SortableMenuItem
                      key={menu.id}
                      menu={menu}
                      isExpanded={isExpanded}
                      onToggle={() => toggleExpand(menu.id)}
                      onEdit={() => openEditMenuDialog(menu)}
                      onDelete={() => openDeleteDialog("menu", menu.id)}
                      onAddSubmenu={() => openNewSubmenuDialog(menu.id)}
                    >
                      {isExpanded && menu.submenus?.length > 0 && (
                        <CardContent className="pt-0">
                          <SortableContext
                            items={menu.submenus.map(s => `${menu.id}-${s.id}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="ml-12 space-y-2 border-l-2 border-slate-200 dark:border-slate-700 pl-4">
                              {menu.submenus.map((submenu) => (
                                <SortableSubmenuItem
                                  key={`${menu.id}-${submenu.id}`}
                                  submenu={submenu}
                                  menuId={menu.id}
                                  onEdit={() => openEditSubmenuDialog(menu.id, submenu)}
                                  onDelete={() => openDeleteDialog("submenu", menu.id, submenu.id)}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </CardContent>
                      )}
                    </SortableMenuItem>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Menu Dialog */}
        <Dialog open={showMenuDialog} onOpenChange={setShowMenuDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderPlus className="w-5 h-5 text-[#0C314C]" />
                {editingMenu ? "Editar Menú" : "Nuevo Menú"}
              </DialogTitle>
              <DialogDescription>
                {editingMenu ? "Modifica los datos del menú" : "Crea un nuevo menú principal"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {!editingMenu && (
                <div className="space-y-2">
                  <Label htmlFor="menu-id">ID del Menú *</Label>
                  <Input
                    id="menu-id"
                    value={menuForm.id}
                    onChange={(e) => setMenuForm({...menuForm, id: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                    placeholder="mi_nuevo_menu"
                    disabled={!!editingMenu}
                  />
                  <p className="text-xs text-slate-500">Identificador único (minúsculas, guiones bajos)</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="menu-label">Nombre *</Label>
                <Input
                  id="menu-label"
                  value={menuForm.label}
                  onChange={(e) => setMenuForm({...menuForm, label: e.target.value})}
                  placeholder="Mi Nuevo Menú"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Icono</Label>
                <Select value={menuForm.icon} onValueChange={(v) => setMenuForm({...menuForm, icon: v})}>
                  <SelectTrigger>
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        {(() => { const Icon = getIconComponent(menuForm.icon); return <Icon className="w-4 h-4" />; })()}
                        {menuForm.icon}
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <ScrollArea className="h-60">
                      {availableIcons.map(icon => {
                        const Icon = getIconComponent(icon);
                        return (
                          <SelectItem key={icon} value={icon}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" />
                              {icon}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="menu-order">Orden</Label>
                <Input
                  id="menu-order"
                  type="number"
                  value={menuForm.order}
                  onChange={(e) => setMenuForm({...menuForm, order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMenuDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleSaveMenu} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Submenu Dialog */}
        <Dialog open={showSubmenuDialog} onOpenChange={setShowSubmenuDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FilePlus className="w-5 h-5 text-[#0C314C]" />
                {editingSubmenu ? "Editar Submenú" : "Nuevo Submenú"}
              </DialogTitle>
              <DialogDescription>
                {editingSubmenu ? "Modifica los datos del submenú" : "Agrega un submenú al menú seleccionado"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {!editingSubmenu && (
                <div className="space-y-2">
                  <Label htmlFor="submenu-id">ID del Submenú *</Label>
                  <Input
                    id="submenu-id"
                    value={submenuForm.id}
                    onChange={(e) => setSubmenuForm({...submenuForm, id: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                    placeholder="mi_submenu"
                    disabled={!!editingSubmenu}
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="submenu-label">Nombre *</Label>
                <Input
                  id="submenu-label"
                  value={submenuForm.label}
                  onChange={(e) => setSubmenuForm({...submenuForm, label: e.target.value})}
                  placeholder="Mi Submenú"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="submenu-path">Ruta *</Label>
                <Input
                  id="submenu-path"
                  value={submenuForm.path}
                  onChange={(e) => setSubmenuForm({...submenuForm, path: e.target.value})}
                  placeholder="/mi-modulo/mi-pagina"
                />
                <p className="text-xs text-slate-500">Ruta de navegación (ej: /mensajeria/solicitudes)</p>
              </div>
              
              <div className="space-y-2">
                <Label>Icono</Label>
                <Select value={submenuForm.icon} onValueChange={(v) => setSubmenuForm({...submenuForm, icon: v})}>
                  <SelectTrigger>
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        {(() => { const Icon = getIconComponent(submenuForm.icon); return <Icon className="w-4 h-4" />; })()}
                        {submenuForm.icon}
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <ScrollArea className="h-60">
                      {availableIcons.map(icon => {
                        const Icon = getIconComponent(icon);
                        return (
                          <SelectItem key={icon} value={icon}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" />
                              {icon}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="submenu-order">Orden</Label>
                <Input
                  id="submenu-order"
                  type="number"
                  value={submenuForm.order}
                  onChange={(e) => setSubmenuForm({...submenuForm, order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubmenuDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleSaveSubmenu} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Confirmar Eliminación
              </DialogTitle>
              <DialogDescription>
                {deleteTarget?.type === "menu" 
                  ? "¿Estás seguro de eliminar este menú y todos sus submenús?"
                  : "¿Estás seguro de eliminar este submenú?"}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleDelete} 
                disabled={saving}
                variant="destructive"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Confirmation Dialog */}
        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <RotateCcw className="w-5 h-5" />
                Restaurar Menús
              </DialogTitle>
              <DialogDescription>
                Esta acción eliminará todos los menús personalizados y restaurará la configuración por defecto. ¿Continuar?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResetDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleReset} 
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Restaurar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
